import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { createCheckoutPayment } from "@/data/checkout";
import { getTrackingMetadata } from "@/utils/tracking";
import type { CreateCheckoutPaymentData } from "@/data/checkout";
import type { PaymentIntent, StripeError } from "@stripe/stripe-js";

/**
 * The one payment flow of the checkout, deferred-intent shaped.
 *
 * The page mounts Elements in deferred mode, so nothing is created on the server until the
 * person actually pays. On Pay:
 *
 *   1. `elements.submit()` validates and tokenises what Stripe collected. If it errors we stop
 *      and say nothing: Stripe has already drawn the sentence under the field it belongs to, and
 *      repeating it in a banner would be the same error twice (checkout 1.4 doNot 5).
 *   2. `createCheckoutPayment` is the existing backend call, unchanged, with the same body it
 *      has always had. It is what applies the promotion code and returns the real amount.
 *   3. `elements.update({ amount })` when the server's amount differs from what the page showed,
 *      so Stripe confirms the figure the server decided and not the one the page guessed.
 *   4. `confirmPayment` with the client secret, `redirect: "if_required"`, so a card that needs
 *      no 3DS never leaves the page and a card that does comes back to the same URL.
 *
 * A total of zero after a full discount never reaches Stripe at all: the backend answers with no
 * client secret and the page goes straight to the receipt.
 */

export type PaymentOutcome = {
  kind: "succeeded" | "processing";
  amountCents: number;
  /** Where the receipt was sent: the form's address, or the one the wallet handed back. */
  email: string;
};

export interface PayInput {
  email: string;
  name?: string;
  phone?: string;
  address?: CreateCheckoutPaymentData["address"];
  promotionCode?: string;
  /** Billing details a wallet handed us, which replace the form's when the wallet paid. */
  walletBillingDetails?: {
    email?: string;
    name?: string;
    phone?: string;
    address?: CreateCheckoutPaymentData["address"];
  };
}

export interface UseCheckoutPaymentOptions {
  paymentLinkId: string;
  currency: string;
  /** What the page is showing as the total, in cents, at the moment Pay is pressed. */
  shownTotalCents: number;
  onDone: (outcome: PaymentOutcome) => void;
}

export function useCheckoutPayment({
  paymentLinkId,
  currency,
  shownTotalCents,
  onDone,
}: UseCheckoutPaymentOptions) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pay = useCallback(
    async (input: PayInput): Promise<boolean> => {
      if (!stripe || !elements) return false;
      setError(null);
      setPaying(true);

      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          // Stripe draws this one under the field it belongs to; nothing to add.
          setPaying(false);
          return false;
        }

        const billing = input.walletBillingDetails;
        const response = await createCheckoutPayment(paymentLinkId, {
          email: billing?.email || input.email,
          name: billing?.name || input.name || undefined,
          phone: billing?.phone || input.phone || undefined,
          address: billing?.address || input.address,
          promotionCode: input.promotionCode || undefined,
          currency,
          metadata: await getTrackingMetadata(),
        });

        const receiptEmail = billing?.email || input.email;

        if (!response.clientSecret && response.amount === 0) {
          onDone({ kind: "succeeded", amountCents: 0, email: receiptEmail });
          return true;
        }

        if (response.amount !== shownTotalCents) {
          elements.update({ amount: Math.max(1, response.amount) });
        }

        const { error: confirmError, paymentIntent } =
          await stripe.confirmPayment({
            elements,
            clientSecret: response.clientSecret,
            confirmParams: {
              return_url: window.location.href,
              payment_method_data: {
                billing_details: {
                  email: billing?.email || input.email || undefined,
                  name: billing?.name || input.name || undefined,
                  phone: billing?.phone || input.phone || undefined,
                  ...(billing?.address || input.address
                    ? {
                        address: toStripeAddress(
                          billing?.address ?? input.address,
                        ),
                      }
                    : {}),
                },
              },
            },
            redirect: "if_required",
          });

        if (confirmError) {
          setError(messageFor(confirmError, t));
          setPaying(false);
          return false;
        }

        const outcome = outcomeFor(
          paymentIntent,
          response.amount,
          receiptEmail,
        );
        if (outcome) {
          onDone(outcome);
          return true;
        }

        setError(t("gymkeePay.checkout.bankRefused"));
        setPaying(false);
        return false;
      } catch {
        setError(t("gymkeePay.checkout.intentFailed"));
        setPaying(false);
        return false;
      }
    },
    [stripe, elements, paymentLinkId, currency, shownTotalCents, onDone, t],
  );

  return { pay, paying, error, setError, ready: !!stripe && !!elements };
}

function outcomeFor(
  intent: PaymentIntent | undefined,
  amountCents: number,
  email: string,
): PaymentOutcome | null {
  if (!intent) return null;
  if (intent.status === "succeeded" || intent.status === "processing") {
    return {
      kind: intent.status,
      amountCents: intent.amount ?? amountCents,
      email,
    };
  }
  return null;
}

/**
 * Stripe's own error, in our words.
 *
 * A card error is a decision the bank made and it is drawn under the field, so only the two
 * cases Stripe reports at the confirm step reach the alert: the card was declined, or the call
 * itself did not go through. Neither prints the raw message: a payer should never be shown
 * "payment_intent_authentication_failure", and a French page should never answer in English.
 */
function messageFor(error: StripeError, t: (key: string) => string): string {
  if (error.type === "card_error" || error.type === "validation_error") {
    return t("gymkeePay.checkout.bankRefused");
  }
  return t("gymkeePay.checkout.intentFailed");
}

function toStripeAddress(address: CreateCheckoutPaymentData["address"]) {
  if (!address) return undefined;
  return {
    line1: address.line1 || null,
    line2: address.line2 || null,
    city: address.city || null,
    state: address.state || null,
    postal_code: address.postal_code || null,
    country: address.country || null,
  };
}
